import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    AlertTriangle, Info, Loader2, RefreshCw, Trash2, Check, ClipboardList, Lock, X,
    ChevronDown, ChevronRight, Download, ImageOff, Link2, Unlink,
} from 'lucide-react';
import { GlassButton, GlassInput, GlassSelect } from '../ui';
import { CopyBtn, CharCount } from './adCopyShared';
import {
    SavedAdCreative, SavedLeadForm, LeadFormData, LeadFormFlag, LandingPage,
    MetaCoverImageResult, MetaLinkablePage, MetaLeadgenForm,
    generateLeadForms, listLeadForms, updateSavedLeadForm, deleteSavedLeadForm,
    getLandingPages, getMetaCoverImage,
    listMetaLinkablePages, listMetaLeadgenForms, setLeadFormMetaLink, clearLeadFormMetaLink,
} from '../../services/api';

// ---------------------------------------------------------------------------
// Constants (mirror lead_form_generator.py)
// ---------------------------------------------------------------------------

const FORM_TYPES = [
    { id: 'more_volume', label: 'More volume' },
    { id: 'higher_intent', label: 'Higher intent' },
    { id: 'rich_creative', label: 'Rich creative' },
];

// META_LOCALES — the 32 valid Meta locale enum values (EN_AU does not exist).
const META_LOCALES = [
    'AR_AR', 'CS_CZ', 'DA_DK', 'DE_DE', 'EL_GR', 'EN_GB', 'EN_US', 'ES_ES',
    'ES_LA', 'FI_FI', 'FR_FR', 'HE_IL', 'HI_IN', 'HU_HU', 'ID_ID', 'IT_IT',
    'JA_JP', 'KO_KR', 'NB_NO', 'NL_NL', 'PL_PL', 'PT_BR', 'PT_PT', 'RO_RO',
    'RU_RU', 'SV_SE', 'TH_TH', 'TR_TR', 'VI_VN', 'ZH_CN', 'ZH_HK', 'ZH_TW',
];

// THANK_YOU_BUTTON_TYPES — mirrors lead_form_generator.THANK_YOU_BUTTON_TYPES (11 values, verbatim).
const THANK_YOU_BUTTON_TYPES = [
    'VIEW_WEBSITE', 'CALL_BUSINESS', 'MESSAGE_BUSINESS', 'DOWNLOAD', 'SCHEDULE_APPOINTMENT',
    'VIEW_ON_FACEBOOK', 'PROMO_CODE', 'NONE', 'WHATSAPP', 'P2B_MESSENGER', 'BOOK_ON_WEBSITE',
];

const LOCALE_LABELS: Record<string, string> = {
    EN_GB: 'English (UK)', EN_US: 'English (US)', DE_DE: 'German', FR_FR: 'French',
    ES_ES: 'Spanish (Spain)', ES_LA: 'Spanish (Latin America)', IT_IT: 'Italian',
    NL_NL: 'Dutch', PT_BR: 'Portuguese (Brazil)', PT_PT: 'Portuguese (Portugal)',
};

// Char limits mirror FORM_SPECS (lead_form_generator.py). source drives badge wording.
const FIELD_LIMITS: Record<string, { limit: number; source: 'meta_documented' | 'aime_house' }> = {
    'name': { limit: 80, source: 'aime_house' },
    'context_card.title': { limit: 60, source: 'aime_house' },
    'context_card.content[]': { limit: 80, source: 'aime_house' },
    'context_card.button_text': { limit: 25, source: 'aime_house' },
    'questions[].label': { limit: 80, source: 'aime_house' },
    'questions[].options[].value': { limit: 40, source: 'aime_house' },
    'thank_you_page.title': { limit: 60, source: 'aime_house' },
    'thank_you_page.body': { limit: 180, source: 'aime_house' },
    'privacy_policy.link_text': { limit: 70, source: 'meta_documented' },
};

// Mirrors the backend's re.fullmatch(r"\d{5,25}", ...) gate — client-side so
// Save link disables before a round-trip, not just after a 400 comes back.
const META_ID_RE = /^\d{5,25}$/;

// Per-reason copy for the degraded-picker advisory (meta_leadgen_forms.py's
// closed reason vocabulary). permission_denied is the EXPECTED outcome for
// most users until Meta's leads_retrieval App Review lands, so it must read
// as "AiMe's limitation, pending" rather than a raw enum value.
const FORMS_UNAVAILABLE_COPY: Record<string, string> = {
    no_page_token: "This Page isn't fully connected — reconnect it in Social Accounts, or enter the form ID manually below.",
    token_expired: "This Page's connection has expired — reconnect it in Social Accounts, or enter the form ID manually below.",
    permission_denied: "AiMe doesn't yet have Meta's permission to list this Page's forms (pending approval) — enter the form ID manually below.",
    unsupported: "Meta doesn't support listing forms for this Page — enter the form ID manually below.",
    graph_error: "Couldn't reach Meta to load forms for this Page right now — enter the form ID manually below.",
};

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    return fallback;
};

const flagsFor = (form: LeadFormData, path: string): LeadFormFlag[] =>
    (form._flags || []).filter(f => f.path === path);

// Recomputes only length-based `over_limit` flags from the current draft,
// using the FIELD_LIMITS map already mirrored client-side (R3). `restricted_risk`
// flags are Python-only keyword heuristics (RESTRICTED_QUESTION_RULES) and are
// deliberately NOT reimplemented here — they pass through verbatim from the
// prior flags array. A stale restricted-risk badge is accepted; a divergent
// second copy of a compliance heuristic would be worse.
const recomputeLengthFlags = (fd: LeadFormData, prevFlags?: LeadFormFlag[]): LeadFormFlag[] => {
    const kept = (prevFlags || []).filter(f => f.kind !== 'over_limit');
    const overLimit: LeadFormFlag[] = [];
    const check = (path: string, value: string | undefined, limitKey: string) => {
        const lim = FIELD_LIMITS[limitKey];
        if (!lim) return;
        const len = (value || '').length;
        if (len > lim.limit) {
            overLimit.push({ kind: 'over_limit', path, limit: lim.limit, actual: len, source: lim.source });
        }
    };
    check('name', fd.name, 'name');
    check('context_card.title', fd.context_card?.title, 'context_card.title');
    check('context_card.button_text', fd.context_card?.button_text, 'context_card.button_text');
    (fd.context_card?.content || []).forEach((block, i) =>
        check(`context_card.content[${i}]`, block, 'context_card.content[]'));
    (fd.questions || []).forEach((q, qi) => {
        if (q.type === 'CUSTOM') {
            check(`questions[${qi}].label`, q.label, 'questions[].label');
            (q.options || []).forEach((o, oi) =>
                check(`questions[${qi}].options[${oi}].value`, o.value, 'questions[].options[].value'));
        }
    });
    check('thank_you_page.title', fd.thank_you_page?.title, 'thank_you_page.title');
    check('thank_you_page.body', fd.thank_you_page?.body, 'thank_you_page.body');
    check('privacy_policy.link_text', fd.privacy_policy?.link_text, 'privacy_policy.link_text');
    return [...kept, ...overLimit];
};

const FlagBadges: React.FC<{ form: LeadFormData; path: string }> = ({ form, path }) => (
    <>
        {flagsFor(form, path).map((f, i) =>
            f.kind === 'over_limit' ? (
                <span key={i} className="ac-over-limit-badge"
                      title={`${f.actual}/${f.limit} chars`}>
                    <AlertTriangle size={11} />{' '}
                    {f.source === 'meta_documented' ? `Over Meta's ${f.limit}-char limit` : 'Longer than recommended'}
                </span>
            ) : (
                <span key={i} className="ac-risk-badge" title={f.detail || f.rule}>
                    <AlertTriangle size={11} /> Restricted-question risk
                </span>
            )
        )}
    </>
);

// Editable field row: same label row (limit/flags/copy) as the old read-only
// FieldRow it replaces (removed — every remaining field is now editable, so it
// has no call sites left), but the value renders as a controlled input/textarea
// instead of a read-only div.
const EditableFieldRow: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    form: LeadFormData;
    path: string;
    limitKey?: string;
    multiline?: boolean;
    rows?: number;
    placeholder?: string;
    disabled?: boolean;
    disabledHint?: string;
}> = ({ label, value, onChange, form, path, limitKey, multiline, rows, placeholder, disabled, disabledHint }) => {
    const lim = limitKey ? FIELD_LIMITS[limitKey] : undefined;
    return (
        <div className="ac-modal-field">
            <div className="ac-modal-field-label">
                {label}
                {lim && <CharCount text={value} limit={lim.limit} />}
                <FlagBadges form={form} path={path} />
                <CopyBtn text={value} />
            </div>
            {multiline ? (
                <textarea
                    className="ac-prompt-textarea"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    rows={rows || 2}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{ width: '100%' }}
                />
            ) : (
                <input
                    className="ac-modal-input"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{ width: '100%' }}
                />
            )}
            {disabled && disabledHint && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {disabledHint}
                </div>
            )}
        </div>
    );
};

// Controlled input for one questions[qi].options[oi].value, nested inside the
// existing .ac-cta-chip pill styling so an editable option keeps the read-only
// pill's look (mirrors the Ad tab's CTA option editing pattern).
const EditableOptionChip: React.FC<{
    value: string;
    onChange: (v: string) => void;
    form: LeadFormData;
    path: string;
}> = ({ value, onChange, form, path }) => (
    <span className="ac-cta-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'default' }}>
        <input
            className="ac-cta-chip-input"
            value={value}
            size={Math.max(value.length, 6)}
            onChange={e => onChange(e.target.value)}
        />
        <CharCount text={value} limit={FIELD_LIMITS['questions[].options[].value'].limit} />
        <FlagBadges form={form} path={path} />
    </span>
);

// ---------------------------------------------------------------------------
// Meta Instant Form link (specs/completed/meta-leadform-linking.md). Manual
// entry always works; the connected-Pages/Graph picker is a best-effort
// enhancement layered on top (degrades to manual entry on any Meta-side or
// Graph failure).
// ---------------------------------------------------------------------------

// Header-status pill. Reads ONLY meta_page_id/meta_form_id as the linked
// predicate — never meta_form_name or meta_linked_at (spec requirement).
const MetaLinkChip: React.FC<{ form: SavedLeadForm }> = ({ form }) => {
    const linked = !!(form.meta_page_id && form.meta_form_id);
    if (!linked) {
        return <span className="ac-meta-link-chip unlinked"><Link2 size={11} /> Not linked</span>;
    }
    const formLabel = form.meta_form_name || form.meta_form_id;
    return (
        <span className="ac-meta-link-chip linked" title={`Meta Page ${form.meta_page_id} · form ${formLabel}`}>
            <Link2 size={11} /> Linked to Meta
        </span>
    );
};

const MetaLinkPanel: React.FC<{
    form: SavedLeadForm;
    onLinked: (updated: SavedLeadForm) => void;
}> = ({ form, onLinked }) => {
    const linked = !!(form.meta_page_id && form.meta_form_id);
    const [editing, setEditing] = useState(false);
    const [manualMode, setManualMode] = useState(false);

    const [pages, setPages] = useState<MetaLinkablePage[]>([]);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [pagesError, setPagesError] = useState<string | null>(null);

    const [selectedPageId, setSelectedPageId] = useState('');
    const [forms, setForms] = useState<MetaLeadgenForm[]>([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [formsUnavailableReason, setFormsUnavailableReason] = useState<string | null>(null);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [selectedFormName, setSelectedFormName] = useState('');

    const [manualPageId, setManualPageId] = useState('');
    const [manualFormId, setManualFormId] = useState('');
    const [manualFormName, setManualFormName] = useState('');

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const loadPages = useCallback(async () => {
        setPagesLoading(true);
        setPagesError(null);
        try {
            const res = await listMetaLinkablePages();
            setPages(res.pages || []);
            if (!res.pages?.length) setManualMode(true); // no connected Pages — go straight to manual
        } catch (e: any) {
            setPagesError(getApiError(e, 'Could not load connected Facebook Pages.'));
            setManualMode(true);
        } finally {
            setPagesLoading(false);
        }
    }, []);

    const loadForms = useCallback(async (pageId: string) => {
        if (!pageId) { setForms([]); setFormsUnavailableReason(null); return; }
        setFormsLoading(true);
        setFormsUnavailableReason(null);
        try {
            const res = await listMetaLeadgenForms(pageId);
            if (res.available) {
                setForms(res.forms || []);
            } else {
                setForms([]);
                setFormsUnavailableReason(res.reason || 'graph_error');
            }
        } catch (e: any) {
            setForms([]);
            setFormsUnavailableReason('graph_error');
        } finally {
            setFormsLoading(false);
        }
    }, []);

    const startEditing = useCallback(() => {
        setEditing(true);
        setManualMode(false);
        setSaveError(null);
        setSelectedPageId(form.meta_page_id || '');
        setSelectedFormId('');
        setSelectedFormName('');
        setManualPageId(form.meta_page_id || '');
        setManualFormId(form.meta_form_id || '');
        setManualFormName(form.meta_form_name || '');
        loadPages();
    }, [form.meta_page_id, form.meta_form_id, form.meta_form_name, loadPages]);

    const handlePageChange = useCallback((pageId: string) => {
        setSelectedPageId(pageId);
        setSelectedFormId('');
        setSelectedFormName('');
        loadForms(pageId);
    }, [loadForms]);

    const handleUnlink = useCallback(async () => {
        if (!window.confirm('Unlink this form from Meta? Incoming leads will no longer be matched to it.')) return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await clearLeadFormMetaLink(form.id);
            onLinked(updated);
            setEditing(false);
        } catch (e: any) {
            setSaveError(getApiError(e, 'Failed to unlink.'));
        } finally {
            setSaving(false);
        }
    }, [form.id, onLinked]);

    const handleSaveLink = useCallback(async () => {
        const pageId = (manualMode ? manualPageId : selectedPageId).trim();
        const formId = (manualMode ? manualFormId : selectedFormId).trim();
        const formName = (manualMode ? manualFormName : selectedFormName).trim();
        if (!pageId || !formId) return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await setLeadFormMetaLink(form.id, {
                page_id: pageId, form_id: formId, form_name: formName || undefined,
            });
            onLinked(updated);
            setEditing(false);
        } catch (e: any) {
            setSaveError(getApiError(e, 'Failed to save link.'));
        } finally {
            setSaving(false);
        }
    }, [form.id, manualMode, manualPageId, manualFormId, manualFormName, selectedPageId, selectedFormId, selectedFormName, onLinked]);

    if (!editing) {
        return (
            <div className="ac-meta-link-panel">
                <div className="ac-meta-link-row">
                    <strong style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
                        Meta form link
                    </strong>
                    {linked && (
                        <span className="ac-meta-link-ids">Page {form.meta_page_id} · form {form.meta_form_name || form.meta_form_id}</span>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <GlassButton variant="secondary" onClick={startEditing} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                            <Link2 size={12} /> {linked ? 'Change' : 'Link to Meta form'}
                        </GlassButton>
                        {linked && (
                            <GlassButton variant="secondary" onClick={handleUnlink} disabled={saving} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                {saving ? <Loader2 size={12} className="spin" /> : <Unlink size={12} />} Unlink
                            </GlassButton>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ac-meta-link-panel">
            <div className="ac-meta-link-row" style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
                    Meta form link
                </strong>
                <button
                    onClick={() => setManualMode(v => !v)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.72rem' }}
                >
                    {manualMode ? 'Use my connected Pages' : 'Enter IDs manually'}
                </button>
            </div>

            {!manualMode ? (
                <>
                    <div style={{ marginBottom: 6 }}>
                        <GlassSelect
                            options={pages.map(p => ({ value: p.page_id, label: p.name }))}
                            value={selectedPageId}
                            onChange={handlePageChange}
                            placeholder={pagesLoading ? 'Loading Pages…' : pages.length ? 'Select a Facebook Page…' : 'No connected Facebook Pages'}
                            disabled={pagesLoading}
                        />
                    </div>
                    {pagesError && <div style={{ fontSize: '0.72rem', color: 'var(--danger)', marginBottom: 6 }}>{pagesError}</div>}

                    {selectedPageId && (
                        <div style={{ marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <div style={{ flex: 1 }}>
                                    <GlassSelect
                                        options={forms.map(f => ({ value: f.id, label: f.status ? `${f.name} (${f.status})` : f.name }))}
                                        value={selectedFormId}
                                        onChange={(v: string) => {
                                            const f = forms.find(x => x.id === v);
                                            setSelectedFormId(v);
                                            setSelectedFormName(f?.name || '');
                                        }}
                                        placeholder={formsLoading ? 'Loading forms…' : 'Select a form…'}
                                        disabled={formsLoading || !!formsUnavailableReason}
                                    />
                                </div>
                                <button
                                    onClick={() => loadForms(selectedPageId)}
                                    disabled={formsLoading}
                                    title="Refresh forms"
                                    style={{ background: 'none', border: '1px solid var(--stroke)', borderRadius: 6, cursor: 'pointer', padding: 6, color: 'var(--text-secondary)' }}
                                >
                                    <RefreshCw size={13} className={formsLoading ? 'spin' : ''} />
                                </button>
                            </div>
                            {formsUnavailableReason && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                    {FORMS_UNAVAILABLE_COPY[formsUnavailableReason] || FORMS_UNAVAILABLE_COPY.graph_error}
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                        <div style={{ flex: 1 }}>
                            <GlassInput placeholder="Facebook Page ID" value={manualPageId} onChange={setManualPageId} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <GlassInput placeholder="Meta form ID" value={manualFormId} onChange={setManualFormId} />
                        </div>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Numeric IDs only — copy them from the Ads Manager URL or the form's details panel.
                    </div>
                    <div style={{ marginBottom: 6 }}>
                        <GlassInput placeholder="Form name (optional, for display only)" value={manualFormName} onChange={setManualFormName} />
                    </div>
                </>
            )}

            {saveError && <div style={{ fontSize: '0.72rem', color: 'var(--danger)', marginBottom: 6 }}>{saveError}</div>}

            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <GlassButton variant="secondary" onClick={() => setEditing(false)} disabled={saving} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                    Cancel
                </GlassButton>
                <GlassButton
                    onClick={handleSaveLink}
                    disabled={saving || (manualMode
                        ? (!META_ID_RE.test(manualPageId.trim()) || !META_ID_RE.test(manualFormId.trim()))
                        : (!selectedPageId || !selectedFormId))}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                >
                    {saving ? <><Loader2 size={12} className="spin" /> Saving…</> : 'Save link'}
                </GlassButton>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// One rendered form, in Meta's builder section order:
// Intro (context_card) → Questions → Privacy → Thank you
// ---------------------------------------------------------------------------

export const LeadFormPreview: React.FC<{
    form: SavedLeadForm;
    tabPrivacyUrl: string;
    onSaved: (updated: SavedLeadForm) => void;
    onDelete: (id: number) => void;
    onRegenerate: (formType: string) => void;
    regenerating: boolean;
}> = ({ form, tabPrivacyUrl, onSaved, onDelete, onRegenerate, regenerating }) => {
    const typeLabel = FORM_TYPES.find(t => t.id === form.form_type)?.label || form.form_type;
    const [cardExpanded, setCardExpanded] = useState(false);
    const [notesDismissed, setNotesDismissed] = useState(false);

    // Single-draft state (mirrors SavedAdPreviewModal's draftCopy/dirty pattern
    // on the Ad tab). `draft` starts as the SAME object reference the parent
    // holds (form.form_data) — every mutator below MUST clone at each level it
    // touches (R6): an in-place write here would corrupt LeadFormsTab.forms too,
    // making Revert appear to work while actually showing edited values.
    const [draft, setDraft] = useState<LeadFormData>(form.form_data);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Privacy URL keeps its own state — it is a separate top-level DB column
    // from form_data.privacy_policy, and the tab-level prefill effect (IMPR-003)
    // below writes to it without that counting as an edit.
    const [privacyDraft, setPrivacyDraft] = useState(form.privacy_policy_url || tabPrivacyUrl || '');
    const [privacyTouched, setPrivacyTouched] = useState(false);
    // Remembers link_text across a URL clear — clearing the URL removes the
    // whole privacy_policy object from form_data on save, which would otherwise
    // lose previously-entered link text if the URL is set again later.
    const [linkTextMemo, setLinkTextMemo] = useState(form.form_data.privacy_policy?.link_text || 'Privacy Policy');

    const fd = draft;

    // Keep the per-form draft synced to the tab-level input after mount (IMPR-003).
    // The useState initializer above only covers the moment this component first
    // renders; the correction path the spec describes is: forms load with no URL,
    // THEN the user types one into the tab-level input — these instances are
    // already mounted by then, so without this effect tabPrivacyUrl never reaches
    // them and the user retypes the URL once per form. Stops syncing once the
    // user edits this form's own field, or once this form already has its own
    // saved URL (a correction to one form must not get silently overwritten by
    // the tab-level value changing for a different form). Deliberately does NOT
    // set `dirty` — a prefill from the tab-level input is not an edit.
    useEffect(() => {
        if (!privacyTouched && !form.privacy_policy_url) {
            setPrivacyDraft(tabPrivacyUrl);
        }
    }, [tabPrivacyUrl, privacyTouched, form.privacy_policy_url]);

    const privacyMissing = !!fd._privacy_url_missing;

    // Mutators — each produces new objects/arrays at every level it touches.
    const setCard = useCallback((k: 'title' | 'button_text', v: string) => {
        setDraft(p => ({ ...p, context_card: { ...p.context_card, [k]: v } }));
        setDirty(true);
    }, []);
    const setBlock = useCallback((i: number, v: string) => {
        setDraft(p => {
            const content = [...(p.context_card?.content || [])];
            content[i] = v;
            return { ...p, context_card: { ...p.context_card, content } };
        });
        setDirty(true);
    }, []);
    const setQLabel = useCallback((qi: number, v: string) => {
        setDraft(p => {
            const questions = [...(p.questions || [])];
            questions[qi] = { ...questions[qi], label: v };
            return { ...p, questions };
        });
        setDirty(true);
    }, []);
    const setOption = useCallback((qi: number, oi: number, v: string) => {
        setDraft(p => {
            const questions = [...(p.questions || [])];
            const options = [...(questions[qi].options || [])];
            options[oi] = { ...options[oi], value: v }; // keeps existing `key` (opt1, opt2…)
            questions[qi] = { ...questions[qi], options };
            return { ...p, questions };
        });
        setDirty(true);
    }, []);
    const setLinkText = useCallback((v: string) => {
        setLinkTextMemo(v);
        setDraft(p => ({ ...p, privacy_policy: { ...(p.privacy_policy || { url: '' }), link_text: v } }));
        setDirty(true);
    }, []);
    const setTY = useCallback((k: 'title' | 'body' | 'button_type' | 'button_text' | 'website_url', v: string) => {
        setDraft(p => ({ ...p, thank_you_page: { ...p.thank_you_page, [k]: v } }));
        setDirty(true);
    }, []);

    // Save is enabled by either an actual field edit, OR a privacy-URL change
    // relative to what's persisted — this covers the case the old standalone
    // button handled: a form with no URL, prefilled by the effect above, that
    // needs nothing else changed (using `dirty` alone would leave Save disabled
    // in exactly that case).
    const canSave = dirty || privacyDraft.trim() !== (form.privacy_policy_url || '');

    const handleSave = useCallback(async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const url = privacyDraft.trim();
            const fdOut: LeadFormData = { ...draft };
            // Compose the privacy fields client-side — the server's sync branch
            // (`update_lead_form`) only mirrors privacy_policy_url into form_data
            // when form_data is ABSENT from the request. Since this save always
            // sends both together, that branch never fires here (R1).
            if (url) {
                fdOut.privacy_policy = {
                    url,
                    link_text: fdOut.privacy_policy?.link_text || linkTextMemo || 'Privacy Policy',
                };
                fdOut._privacy_url_missing = false;
            } else {
                delete (fdOut as any).privacy_policy;
                fdOut._privacy_url_missing = true;
            }
            fdOut._flags = recomputeLengthFlags(fdOut, draft._flags);
            const updated = await updateSavedLeadForm(form.id, {
                form_data: fdOut,
                privacy_policy_url: url || null,
            });
            // Guard against a malformed 200 (missing form_data) blanking the card.
            if (updated?.form_data) {
                setDraft(updated.form_data);
                setLinkTextMemo(updated.form_data.privacy_policy?.link_text || linkTextMemo);
            }
            setDirty(false);
            onSaved(updated);
        } catch (e: any) {
            // dirty/draft are deliberately NOT reset on failure — edits survive
            // on screen and Save Changes stays enabled for a retry.
            setSaveError(getApiError(e, 'Failed to save changes.'));
        } finally {
            setSaving(false);
        }
    }, [form.id, draft, privacyDraft, linkTextMemo, onSaved]);

    const handleRevert = useCallback(() => {
        setDraft(form.form_data);
        setPrivacyDraft(form.privacy_policy_url || '');
        setPrivacyTouched(false);
        setLinkTextMemo(form.form_data.privacy_policy?.link_text || 'Privacy Policy');
        setDirty(false);
        setSaveError(null);
    }, [form.form_data, form.privacy_policy_url]);

    const handleRegenerateClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        // A privacy-URL edit is a real pending change (see canSave above) even
        // though it lives outside `dirty` — gated on `privacyTouched` so the
        // tab-level prefill effect setting `privacyDraft` on its own never
        // trips a false-positive confirm (nothing would be lost in that case,
        // since runGenerate sends that same tab-level value anyway).
        const pendingPrivacy = privacyTouched && privacyDraft.trim() !== (form.privacy_policy_url || '');
        // Linked forms get an UNCONDITIONAL confirm regardless of dirty state —
        // regenerating replaces the row (delete-then-insert) and silently drops
        // the Meta link, which is a real live-integration consequence, not just
        // a discarded edit.
        if (form.meta_page_id && form.meta_form_id) {
            if (!window.confirm('This form is linked to a live Meta form. Regenerating replaces it with a new form and clears that link. Continue?')) return;
        } else if ((dirty || pendingPrivacy) && !window.confirm('You have unsaved edits to this form. Regenerating replaces it and discards them. Continue?')) {
            return;
        }
        onRegenerate(form.form_type);
    }, [dirty, privacyTouched, privacyDraft, form.privacy_policy_url, form.meta_page_id, form.meta_form_id, onRegenerate, form.form_type]);

    return (
        <div className="ac-leadform-card" style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <div
                onClick={() => setCardExpanded(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: cardExpanded ? 8 : 0, flexWrap: 'wrap', cursor: 'pointer' }}
            >
                {cardExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <ClipboardList size={14} />
                <strong style={{ fontSize: '0.85rem' }}>{typeLabel}</strong>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{fd.name}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', opacity: 0.8 }}>{fd.locale}</span>
                {fd._grounding_partial && (
                    <span className="ac-risk-badge" title="This ad has no saved offer or audience segments — the form is grounded on its copy alone. Consider filling those in on the Ad tab first.">
                        <Info size={11} /> Partial grounding
                    </span>
                )}
                <MetaLinkChip form={form} />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <GlassButton variant="secondary" onClick={handleRegenerateClick} disabled={regenerating}
                                 style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                        {regenerating ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />} Regenerate
                    </GlassButton>
                    <button className="ac-delete-btn" title="Delete"
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (window.confirm('Delete this lead form?')) onDelete(form.id); }}>
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            {cardExpanded && (
                <>
                    {!notesDismissed && (fd._setup_notes?.length ?? 0) > 0 && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 6, padding: '6px 10px', marginBottom: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>{fd._setup_notes.map((n, i) => <div key={i}>{n}</div>)}</div>
                            <button onClick={() => setNotesDismissed(true)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} title="Dismiss">
                                <X size={12} />
                            </button>
                        </div>
                    )}

                    <MetaLinkPanel form={form} onLinked={onSaved} />

                    {/* Intro — the context card IS the intro screen, present on every type */}
                    <div className="ac-leadform-section">
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>Intro</div>
                        <EditableFieldRow label="Headline" value={fd.context_card?.title || ''} onChange={v => setCard('title', v)} form={fd} path="context_card.title" limitKey="context_card.title" />
                        {(fd.context_card?.content || []).map((block, i) => (
                            <EditableFieldRow key={i} label={`Description ${i + 1}`} value={block} onChange={v => setBlock(i, v)} form={fd} path={`context_card.content[${i}]`} limitKey="context_card.content[]" multiline rows={2} />
                        ))}
                        <EditableFieldRow label="Button" value={fd.context_card?.button_text || ''} onChange={v => setCard('button_text', v)} form={fd} path="context_card.button_text" limitKey="context_card.button_text" />
                    </div>

                    {/* Questions */}
                    <div className="ac-leadform-section">
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>Questions</div>
                        {(fd.questions || []).map((q, qi) => q.type === 'CUSTOM' ? (
                            <div key={qi} className="ac-modal-field">
                                <div className="ac-modal-field-label">
                                    Question {qi + 1}
                                    <CharCount text={q.label || ''} limit={FIELD_LIMITS['questions[].label'].limit} />
                                    <FlagBadges form={fd} path={`questions[${qi}].label`} />
                                    <CopyBtn text={q.label || ''} />
                                </div>
                                <input
                                    className="ac-modal-input"
                                    style={{ width: '100%' }}
                                    value={q.label || ''}
                                    onChange={e => setQLabel(qi, e.target.value)}
                                />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                                    {(q.options || []).map((o, oi) => (
                                        <EditableOptionChip key={oi} value={o.value} onChange={v => setOption(qi, oi, v)} form={fd} path={`questions[${qi}].options[${oi}].value`} />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <span key={qi} style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginRight: 8 }}>
                                {q.type.replace(/_/g, ' ').toLowerCase()} (auto-filled)
                            </span>
                        ))}
                    </div>

                    {/* Privacy */}
                    <div className="ac-leadform-section">
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>Privacy</div>
                        {privacyMissing && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                <Lock size={12} /> No privacy policy URL — Meta's form builder requires one before this form can be used.
                            </div>
                        )}
                        <EditableFieldRow
                            label="Link text"
                            value={fd.privacy_policy?.link_text ?? linkTextMemo}
                            onChange={setLinkText}
                            form={fd}
                            path="privacy_policy.link_text"
                            limitKey="privacy_policy.link_text"
                            disabled={!privacyDraft.trim()}
                            disabledHint="Add a privacy policy URL below to edit the link text — there's nowhere to save it without one."
                        />
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                            <div style={{ flex: 1 }}>
                                <GlassInput placeholder="https://…/privacy" value={privacyDraft}
                                            onChange={v => { setPrivacyDraft(v); setPrivacyTouched(true); }} />
                            </div>
                        </div>
                    </div>

                    {/* Thank you */}
                    <div className="ac-leadform-section">
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>Thank you</div>
                        <EditableFieldRow label="Headline" value={fd.thank_you_page?.title || ''} onChange={v => setTY('title', v)} form={fd} path="thank_you_page.title" limitKey="thank_you_page.title" />
                        <EditableFieldRow label="Body" value={fd.thank_you_page?.body || ''} onChange={v => setTY('body', v)} form={fd} path="thank_you_page.body" limitKey="thank_you_page.body" multiline rows={3} />
                        <div className="ac-modal-field">
                            <div className="ac-modal-field-label">
                                Button type
                                {!!fd.tracking_parameters && (
                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                                        locked — the auto-tagged link needs VIEW_WEBSITE
                                    </span>
                                )}
                            </div>
                            {/* Same guard as website_url below (XF-IMPR-001): switching the
                                button type off VIEW_WEBSITE would kill the tagged link the
                                URL lock exists to protect. */}
                            <GlassSelect
                                options={THANK_YOU_BUTTON_TYPES.map(t => ({ value: t, label: t }))
                                    .concat(THANK_YOU_BUTTON_TYPES.includes(fd.thank_you_page?.button_type) ? [] : [{ value: fd.thank_you_page?.button_type || '', label: fd.thank_you_page?.button_type || '(none)' }])}
                                value={fd.thank_you_page?.button_type || ''}
                                onChange={v => setTY('button_type', v)}
                                disabled={!!fd.tracking_parameters}
                            />
                        </div>
                        <EditableFieldRow label="Button text" value={fd.thank_you_page?.button_text || ''} onChange={v => setTY('button_text', v)} form={fd} path="thank_you_page.button_text" />
                        {/* Cross-feature guard: a Rail-A auto-tagged thank-you URL carries the
                            utm params that make post-submission visits attributable AND joins a
                            utm_links history row — hand-editing it here would silently break
                            both. Locked (not hidden) so the value stays copyable; change the
                            destination by regenerating the form. tracking_parameters is the
                            exact marker: lead_form_generator sets it and the tagged URL under
                            the same `if thank_you_url:` — testing the draft URL instead would
                            freeze the input mid-keystroke on legitimately-editable forms
                            (XF-BLOCKER-001). */}
                        <EditableFieldRow label="Website URL" value={fd.thank_you_page?.website_url || ''} onChange={v => setTY('website_url', v)} form={fd} path="thank_you_page.website_url" placeholder="https://…"
                            disabled={!!fd.tracking_parameters}
                            disabledHint="Auto-tagged thank-you link — regenerate the form with a different destination to change it." />
                        {fd.thank_you_page?.website_url && !/^https?:\/\//i.test(fd.thank_you_page.website_url) && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--warning)', marginTop: -6, marginBottom: 8 }}>
                                Should start with http:// or https://
                            </div>
                        )}
                        {fd.tracking_parameters && (
                            <div className="ac-modal-field">
                                <div className="ac-modal-field-label">
                                    Tracking parameters
                                    {/* Paste-ready query string — values URI-encoded so a free-text
                                        utm_campaign containing &/=/# can't break the structure
                                        (IMPR-009); the display line below stays human-readable. */}
                                    <CopyBtn text={`aime_saved_ad_id=${encodeURIComponent(fd.tracking_parameters.aime_saved_ad_id)}&form_type=${encodeURIComponent(fd.tracking_parameters.form_type)}&utm_campaign=${encodeURIComponent(fd.tracking_parameters.utm_campaign)}`} />
                                </div>
                                <div className="ac-modal-field-text">
                                    {`aime_saved_ad_id=${fd.tracking_parameters.aime_saved_ad_id}, form_type=${fd.tracking_parameters.form_type}, utm_campaign=${fd.tracking_parameters.utm_campaign}`}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="ac-modal-footer">
                        <GlassButton variant="secondary" onClick={handleRevert} disabled={!canSave || saving}>
                            Revert
                        </GlassButton>
                        <GlassButton variant="primary" onClick={handleSave} disabled={!canSave || saving}>
                            {saving ? <><Loader2 size={13} className="spin" /> Saving…</> : <><Check size={13} /> Save Changes</>}
                        </GlassButton>
                    </div>
                    {saveError && <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: 4 }}>{saveError}</div>}
                </>
            )}
        </div>
    );
};

// Right-hand panel of the Generate-Forms row: a clean (no-overlay) preview of
// the ad's base image, resized server-side to a real 1080px-wide PNG for
// Meta's Instant Form cover-image upload, with a direct download. NOTE:
// PLATFORM_IMAGE_SPECS["meta"] in ad_copy_generator.py declares 1080x1080,
// but that's prompt text only — the actual generation backends return
// 1024x1024, which is why a real server-side resize (meta_cover_image.py) is
// needed rather than just linking the existing base image.
const MetaCoverImagePanel: React.FC<{ creative: SavedAdCreative }> = ({ creative }) => {
    const baseUrl = creative.overlay_state?.base_image_url || creative.image_url;
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<MetaCoverImageResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!baseUrl) { setResult(null); setError(null); return; }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await getMetaCoverImage(creative.id);
                if (!cancelled) setResult(res);
            } catch (e: any) {
                if (!cancelled) setError(getApiError(e, 'Cover image unavailable right now.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [creative.id, baseUrl]);

    const emptyReason = (): string => {
        if (!baseUrl) return "This ad has no image yet. Generate one from the Ad Copy tab to get a form cover image.";
        if (error) return 'Cover image unavailable right now.';
        if (result?.reason === 'missing_file') return "The ad's image file could not be found on the server.";
        if (result?.reason === 'remote_base' || result?.reason === 'unsupported_path') {
            return "This ad's image isn't an AIME-generated file, so no cover variant can be made.";
        }
        if (result?.reason === 'overlay_baked_base') {
            return "This ad's overlay was applied before AIME tracked the original image, so a clean cover can't be derived. Regenerate the image to enable it.";
        }
        return 'Cover image unavailable right now.';
    };

    return (
        <div style={{
            background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 8,
            padding: 12, flex: '0 1 220px', minWidth: 200,
        }}>
            <div className="ac-modal-field-label" style={{ marginBottom: 6 }}>Form cover image</div>
            {loading ? (
                <div style={{
                    aspectRatio: '1 / 1', display: 'grid', placeItems: 'center',
                    background: 'var(--panel)', border: '1px dashed var(--stroke)', borderRadius: 6,
                    color: 'var(--text-secondary)',
                }}>
                    <Loader2 size={20} className="spin" />
                </div>
            ) : (result?.available && result.url) ? (
                <>
                    <img src={result.url} alt="Ad base image, no text overlay"
                         style={{ width: '100%', borderRadius: 6, border: '1px solid var(--stroke)', display: 'block' }} />
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', margin: '6px 0' }}>
                        For Meta's Instant Form cover. Your ad's image without the text overlay, scaled to{' '}
                        {result.width}×{result.height}
                        {result.source_width && result.source_height
                            ? ` from the ${result.source_width}×${result.source_height} original.`
                            : '.'}
                    </div>
                    <a href={result.url} download={`meta-form-cover-${creative.id}.png`}
                       className="ac-download-btn" style={{ width: '100%', justifyContent: 'center' }}>
                        <Download size={13} /> Download 1080px
                    </a>
                </>
            ) : (
                <div style={{
                    aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 6, textAlign: 'center',
                    background: 'var(--panel)', border: '1px dashed var(--stroke)', borderRadius: 6,
                    color: 'var(--text-secondary)', padding: 10,
                }}>
                    <ImageOff size={22} />
                    <span style={{ fontSize: '0.72rem' }}>{emptyReason()}</span>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// The Lead Forms tab body — owns ONLY forms state (never the Ad tab's drafts).
// ---------------------------------------------------------------------------

const LeadFormsTab: React.FC<{
    creative: SavedAdCreative;
    dirty: boolean;
    brandGuideId?: string;
    // True when Regenerate Copy replaced the ad's saved copy this session —
    // existing forms are grounded on copy that no longer exists (XF-IMPR-001).
    copyReplaced?: boolean;
    onStaleFormsHandled?: () => void;
    onFormsCountChange: (n: number) => void;
    projectId?: string;
}> = ({ creative, dirty, brandGuideId, copyReplaced, onStaleFormsHandled, onFormsCountChange, projectId }) => {
    const [forms, setForms] = useState<SavedLeadForm[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [generating, setGenerating] = useState(false);
    const [regeneratingType, setRegeneratingType] = useState<string | null>(null);
    const [genError, setGenError] = useState<string | null>(null);
    const [typeErrors, setTypeErrors] = useState<{ form_type: string; error: string }[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>(FORM_TYPES.map(t => t.id));
    const [privacyUrl, setPrivacyUrl] = useState('');
    const [hint, setHint] = useState('');
    const [locale, setLocale] = useState('EN_GB');
    // Rail A — thank-you destination (mutually exclusive modes; server 400s both)
    const [destMode, setDestMode] = useState<'none' | 'page' | 'url'>('none');
    const [destPageId, setDestPageId] = useState('');
    const [destUrl, setDestUrl] = useState('');
    const [utmCampaign, setUtmCampaign] = useState('');
    const [pages, setPages] = useState<LandingPage[]>([]);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [pagesLoaded, setPagesLoaded] = useState(false);

    useEffect(() => {
        if (destMode !== 'page' || pagesLoaded || !projectId) return;
        let cancelled = false;
        (async () => {
            setPagesLoading(true);
            try {
                const data = await getLandingPages(projectId, 'published');
                if (!cancelled) { setPages(data); setPagesLoaded(true); }
            } catch { /* select shows empty; server still validates */ }
            finally { if (!cancelled) setPagesLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [destMode, pagesLoaded, projectId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setListError(null);
            try {
                const data = await listLeadForms({ saved_ad_id: creative.id });
                if (!cancelled) { setForms(data); setLoaded(true); }
            } catch (e: any) {
                // IMPR-004: don't swallow this — a 403/500/network error must not
                // leave the tab blank with no explanation and no retry. `loaded`
                // stays false on error so the "No lead forms yet" empty state
                // (which implies "you have zero forms", a different claim) never
                // renders in its place.
                if (!cancelled) setListError(getApiError(e, 'Failed to load lead forms.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [creative.id, reloadNonce]);

    // Badge count is fired from an effect on forms.length (IMPR-001), not from
    // inside a setForms updater. Updaters run during React's render phase, and
    // calling onFormsCountChange there (which ripples up to setSavedCreatives/
    // setPreviewCreative on the grandparent) produced "Cannot update a component
    // while rendering a different component" and double-fired under StrictMode.
    // Gated on `loaded` so the pre-fetch empty array never reports a false 0 and
    // clobbers the badge the parent already shows from creative.lead_forms_count.
    //
    // BLOCKER-001 (review iteration 2): `onFormsCountChange` is an inline arrow
    // at the call site (AdCopyModule.tsx) that writes new object/array refs onto
    // grandparent state, so its identity churns on every render — a naive
    // dep-array effect fires every render forever. Fixed with a ref that
    // remembers the last count actually REPORTED (not the last count seen): the
    // effect still re-runs whenever the unstable callback identity changes, but
    // it only *calls* it (and only then triggers a parent setState) when the
    // count itself differs from what was last reported. That makes the second
    // firing of any loop a no-op regardless of the parent ever memoising its
    // callback. The ref starts at `null` so the first real report (post-load)
    // always fires even though the primitive count could legitimately be 0.
    //
    // Secondary consequence fix (same review note): `loaded` is kept — dropping
    // it would let the pre-fetch empty render's forms.length===0 report a false
    // 0 and clobber the parent's `creative.lead_forms_count` badge before the
    // list fetch even resolves. Instead `applyResults` (below) also flips
    // `loaded` to true, since a successful generate response is real data just
    // like a successful list fetch — so a generate after a failed initial fetch
    // now propagates the count too.
    const lastReportedCountRef = useRef<number | null>(null);
    useEffect(() => {
        if (!loaded) return;
        if (lastReportedCountRef.current === forms.length) return;
        lastReportedCountRef.current = forms.length;
        onFormsCountChange(forms.length);
    }, [forms.length, loaded, onFormsCountChange]);

    const applyResults = useCallback((
        requestedTypes: string[],
        newForms: SavedLeadForm[],
        errors: { form_type: string; error: string }[],
    ) => {
        setForms(prev => {
            const replacedTypes = new Set(newForms.map(f => f.form_type));
            return [...newForms, ...prev.filter(f => !replacedTypes.has(f.form_type))];
        });
        // A generate response is real data, same as a successful list fetch —
        // flip `loaded` here too so the count effect above fires even when the
        // initial list fetch had failed (see BLOCKER-001 fix note above).
        setLoaded(true);
        // Merge per form_type instead of replacing the whole array (IMPR-002): a
        // single-type regenerate only knows about the type(s) it just re-ran, so
        // drop those entries and keep every other type's still-standing error row.
        setTypeErrors(prev => [
            ...prev.filter(e => !requestedTypes.includes(e.form_type)),
            ...errors,
        ]);
    }, []);

    const runGenerate = useCallback(async (types: string[]) => {
        setGenError(null);
        try {
            const res = await generateLeadForms(creative.id, {
                form_types: types,
                privacy_policy_url: privacyUrl.trim() || undefined,
                custom_hint: hint.trim() || undefined,
                brand_guide_id: brandGuideId || undefined,
                locale,
                thank_you_url: destMode === 'url' ? (destUrl.trim() || undefined) : undefined,
                thank_you_landing_page_id: destMode === 'page' ? (destPageId || undefined) : undefined,
                utm_campaign: utmCampaign.trim() || undefined,
            });
            applyResults(types, res.forms, res.errors);
            if (res.privacy_url_suggestion && !privacyUrl.trim()) setPrivacyUrl(res.privacy_url_suggestion);
            // Freshly generated forms are grounded on the CURRENT copy, so the
            // stale-forms advisory (copyReplaced) is resolved. Regenerating a
            // single type clears it too — an advisory, not a per-form ledger.
            if (res.forms.length > 0) onStaleFormsHandled?.();
        } catch (e: any) {
            setGenError(getApiError(e, 'Generation failed.'));
        }
    }, [creative.id, privacyUrl, hint, locale, brandGuideId, applyResults, onStaleFormsHandled,
        destMode, destPageId, destUrl, utmCampaign]);

    const handleGenerate = useCallback(async () => {
        setGenerating(true);
        await runGenerate(selectedTypes);
        setGenerating(false);
    }, [runGenerate, selectedTypes]);

    const handleRegenerate = useCallback(async (formType: string) => {
        setRegeneratingType(formType);
        await runGenerate([formType]);
        setRegeneratingType(null);
    }, [runGenerate]);

    const handleDelete = useCallback(async (id: number) => {
        try {
            await deleteSavedLeadForm(id);
            setForms(prev => prev.filter(f => f.id !== id));
        } catch { alert('Delete failed.'); }
    }, []);

    const handleSaved = useCallback((updated: SavedLeadForm) => {
        setForms(prev => prev.map(f => f.id === updated.id ? updated : f));
    }, []);

    const toggleType = (id: string) =>
        setSelectedTypes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

    const missingCount = forms.filter(f => f.form_data._privacy_url_missing).length;
    const localeOptions = META_LOCALES.map(c => ({ value: c, label: LOCALE_LABELS[c] || c }));

    return (
        <div style={{ padding: '14px 20px', overflowY: 'auto' }}>
            {/* Generate controls + form cover image side by side */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 8, padding: 12, flex: '1 1 340px', minWidth: 0 }}>
                <div className="ac-modal-field-label" style={{ marginBottom: 6 }}>Form types</div>
                <div className="ac-cta-chips" style={{ marginBottom: 10 }}>
                    {FORM_TYPES.map(t => (
                        <button key={t.id}
                                className={`ac-cta-chip ${selectedTypes.includes(t.id) ? 'selected' : ''}`}
                                onClick={() => toggleType(t.id)}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 10, marginBottom: 10 }}>
                    <div>
                        <div className="ac-modal-field-label" style={{ marginBottom: 4 }}>Privacy Policy URL</div>
                        <GlassInput placeholder="https://…/privacy" value={privacyUrl} onChange={setPrivacyUrl} />
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                            Unverified suggestion — confirm this is the business's real privacy policy URL before publishing.
                        </div>
                    </div>
                    <div>
                        <div className="ac-modal-field-label" style={{ marginBottom: 4 }}>Form language</div>
                        <GlassSelect options={localeOptions} value={locale} onChange={setLocale} />
                    </div>
                </div>
                <div className="ac-modal-field-label" style={{ marginBottom: 4 }}>Thank-you destination</div>
                <div className="ac-cta-chips" style={{ marginBottom: 8 }}>
                    {([['none', 'None'], ['page', 'Landing page'], ['url', 'Custom URL']] as const).map(([m, lbl]) => (
                        <button key={m}
                                className={`ac-cta-chip ${destMode === m ? 'selected' : ''}`}
                                onClick={() => setDestMode(m)}>
                            {lbl}
                        </button>
                    ))}
                </div>
                {destMode === 'page' && (
                    <div style={{ marginBottom: 8 }}>
                        <GlassSelect
                            options={[{ value: '', label: pagesLoading ? 'Loading…' : (pages.length ? '— Select a published page —' : 'No published landing pages') },
                                      ...pages.map(p => ({ value: p.landing_page_id, label: p.title }))]}
                            value={destPageId}
                            onChange={setDestPageId}
                        />
                    </div>
                )}
                {destMode === 'url' && (
                    <div style={{ marginBottom: 8 }}>
                        <GlassInput placeholder="https://…/thank-you" value={destUrl} onChange={setDestUrl} />
                    </div>
                )}
                {destMode !== 'none' && (
                    <div style={{ marginBottom: 10 }}>
                        <div className="ac-modal-field-label" style={{ marginBottom: 4 }}>UTM campaign (optional)</div>
                        <GlassInput placeholder="defaults to a slug of the ad's label" value={utmCampaign} onChange={setUtmCampaign} />
                        <div className="ac-modal-field-text" style={{ marginTop: 4, fontSize: '0.72rem' }}>
                            ?utm_source=facebook&utm_medium=lead_form&utm_campaign={utmCampaign.trim() || '(from ad label)'}&utm_content=&lt;form type&gt;
                        </div>
                    </div>
                )}
                <div className="ac-modal-field-label" style={{ marginBottom: 4 }}>Extra instruction (optional)</div>
                <textarea
                    className="ac-prompt-textarea"
                    value={hint}
                    onChange={e => setHint(e.target.value)}
                    placeholder="e.g. qualify on business ownership"
                    rows={2}
                    style={{ width: '100%', padding: '8px 10px', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 6, color: 'var(--text)', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <GlassButton variant="primary" onClick={handleGenerate}
                                 disabled={generating || dirty || selectedTypes.length === 0
                                     || (destMode === 'page' && !destPageId)
                                     || (destMode === 'url' && !destUrl.trim())}>
                        {generating ? <><Loader2 size={13} className="spin" /> Generating…</> : <><ClipboardList size={13} /> Generate Forms</>}
                    </GlassButton>
                    {dirty && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Save your copy changes first — forms are generated from the saved ad.
                        </span>
                    )}
                </div>
                {genError && <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: 6 }}>{genError}</div>}
            </div>
            <MetaCoverImagePanel creative={creative} />
            </div>

            {copyReplaced && forms.length > 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--warning)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={13} /> The ad's copy was regenerated after these forms were created — they're grounded on the previous copy. Regenerate the forms to match.
                </div>
            )}

            {missingCount > 1 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--warning)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={13} /> {missingCount} of {forms.length} forms still need a privacy policy URL.
                </div>
            )}

            {typeErrors.map(err => (
                <div key={err.form_type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--danger)', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
                    <AlertTriangle size={13} />
                    {FORM_TYPES.find(t => t.id === err.form_type)?.label || err.form_type}: {err.error}
                    <GlassButton variant="secondary" onClick={() => handleRegenerate(err.form_type)}
                                 disabled={regeneratingType === err.form_type}
                                 style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: '0.72rem' }}>
                        <RefreshCw size={11} /> Regenerate
                    </GlassButton>
                </div>
            ))}

            {loading && <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Loading…</p>}
            {listError && !loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--danger)', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
                    <AlertTriangle size={13} />
                    {listError}
                    <GlassButton variant="secondary" onClick={() => setReloadNonce(n => n + 1)}
                                 style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: '0.72rem' }}>
                        <RefreshCw size={11} /> Retry
                    </GlassButton>
                </div>
            )}
            {loaded && !loading && !listError && forms.length === 0 && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>
                    No lead forms yet. Pick the form types above and click Generate Forms.
                </p>
            )}
            {forms.map(f => (
                <LeadFormPreview
                    key={f.id}
                    form={f}
                    tabPrivacyUrl={privacyUrl}
                    onSaved={handleSaved}
                    onDelete={handleDelete}
                    onRegenerate={handleRegenerate}
                    regenerating={regeneratingType === f.form_type}
                />
            ))}
        </div>
    );
};

export default LeadFormsTab;
